/*
 * The Ultimate Preset Pack's bonus files, drawn flat
 * (snippets/ultimate-pack-bonus-art.liquid, styled in
 * assets/ultimate-pack-bonus-art.css).
 *
 * [data-live] on a picture plays its moment: while the pointer is over its
 * card on a computer; on a touch screen once the card is in view (the four
 * take turns), and a tap plays or rests it. The pointer also nudges the
 * picture a little (--px, --py from -1 to 1).
 *
 * When it rests, its loops ease back to the still picture instead of
 * jumping there.
 *
 * The bonus preset tile's two knobs can be dragged along their tracks, or
 * sent to a point on one with a press (a knob once moved by hand stays put),
 * and a Photoshop file clicked comes to the front (on a touch screen, a tap
 * on the front one plays or rests the picture).
 */
(() => {
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  const pointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  // the "14×" badge counts up each time the picture plays
  const countUp = (art) => {
    const counter = art.querySelector('[data-art-count]');
    if (!counter) return;
    const target = parseInt(counter.dataset.artCount, 10);
    if (Number.isNaN(target) || still.matches) {
      counter.textContent = target;
      return;
    }
    // hold the final number's width (in em, so it scales with the card)
    if (!counter.style.minWidth) {
      counter.textContent = target;
      const em = counter.getBoundingClientRect().width / parseFloat(getComputedStyle(counter).fontSize);
      if (em > 0) counter.style.minWidth = `${em.toFixed(3)}em`;
    }
    cancelAnimationFrame(counter.raf);
    let start = null;
    const frame = (now) => {
      if (start === null) start = now;
      const t = Math.max(0, Math.min((now - start) / 900, 1));
      const text = String(Math.max(1, Math.round(target * (1 - Math.pow(1 - t, 3)))));
      if (counter.textContent !== text) counter.textContent = text;
      if (t < 1) counter.raf = requestAnimationFrame(frame);
    };
    counter.raf = requestAnimationFrame(frame);
  };

  // the loops playing now, with what each shows at this moment
  const playing = (art) =>
    art
      .getAnimations({ subtree: true })
      .filter((animation) => animation.animationName && animation.playState === 'running')
      .map((animation) => {
        const target = animation.effect.target;
        const style = getComputedStyle(target);
        const shown = { offset: 0 };
        animation.effect.getKeyframes().forEach((keyframe) =>
          Object.keys(keyframe).forEach((name) => {
            if (!['offset', 'computedOffset', 'easing', 'composite'].includes(name)) shown[name] = style[name];
          })
        );
        return { animation, target, shown };
      });

  // Keep return animations separate from the CSS loops so re-entering a
  // card cannot leave an old return animation fighting the new interaction.
  const settling = new WeakMap();
  const live = (art, on) => {
    if (art.hasAttribute('data-live') === on) return;
    const was = on || still.matches || !art.getAnimations ? [] : playing(art);
    (settling.get(art) || []).forEach((animation) => animation.cancel());
    settling.delete(art);
    art.toggleAttribute('data-live', on);
    if (on) countUp(art);

    const returns = [];
    was.forEach(({ animation, target, shown }) => {
      // Reading the resting style also flushes the removal of the CSS loop.
      const style = getComputedStyle(target);
      const ring = target.classList.contains('upp-art__ring');
      const rest = { offset: 1 };
      Object.keys(shown).forEach((name) => {
        if (name !== 'offset') rest[name] = style[name];
      });
      if (animation.playState !== 'idle' && !ring) return;
      // Sound waves keep travelling out as they fade rather than shrinking
      // back into the speaker. The rings coast while their CSS fade finishes.
      if (target.classList.contains('spk-wave')) rest.transform = 'scale(1.55)';
      if (ring) rest.rotate = `${(parseFloat(shown.rotate) || 0) + 14}deg`;
      try {
        const returning = target.animate([shown, rest], {
          duration: ring ? 700 : 850,
          easing: 'cubic-bezier(0.22, 0.8, 0.25, 1)'
        });
        returns.push(returning);
      } catch (error) {
        // Keep the resting CSS picture if Web Animations is unavailable.
      }
    });
    if (returns.length) settling.set(art, returns);
  };

  // drag a knob of the preset tile along its track (in the tile's own units)
  const knobs = (art) => {
    const tracks = art.querySelectorAll('[data-art-track]');
    art.querySelectorAll('[data-art-knob]').forEach((knob, index) => {
      const svg = knob.ownerSVGElement;
      const min = parseFloat(knob.dataset.min);
      const max = parseFloat(knob.dataset.max);
      const home = parseFloat(knob.dataset.x);
      let from = null;
      const at = (event) => {
        const point = svg.createSVGPoint();
        point.x = event.clientX;
        point.y = event.clientY;
        return point.matrixTransform(svg.getScreenCTM().inverse()).x;
      };
      // pick it up where it is now, even mid-slide, and keep it there; a
      // press on its track brings it under the pointer
      const grab = (event, jump) => {
        event.preventDefault();
        event.stopPropagation();
        knob.setPointerCapture(event.pointerId);
        const now = new DOMMatrixReadOnly(getComputedStyle(knob).transform).e;
        knob.setAttribute('data-touched', '');
        knob.setAttribute('data-dragging', '');
        from = jump ? 0 : at(event) - now - home;
        const x = Math.max(min, Math.min(max, at(event) - from));
        knob.style.setProperty('--drag', `${(jump ? x - home : now).toFixed(2)}px`);
      };
      knob.addEventListener('pointerdown', (event) => grab(event, false));
      const track = tracks[index];
      if (track) {
        track.addEventListener('pointerdown', (event) => grab(event, true));
        track.addEventListener('click', (event) => event.stopPropagation());
      }
      knob.addEventListener('pointermove', (event) => {
        if (from === null) return;
        const x = Math.max(min, Math.min(max, at(event) - from));
        knob.style.setProperty('--drag', `${(x - home).toFixed(2)}px`);
      });
      const drop = () => {
        from = null;
        knob.removeAttribute('data-dragging');
      };
      knob.addEventListener('pointerup', drop);
      knob.addEventListener('pointercancel', drop);
      // a tap on a knob is not a tap on the card (which would rest it)
      knob.addEventListener('click', (event) => event.stopPropagation());
    });
  };

  // a Photoshop file clicked comes to the front; on a touch screen a tap on
  // the front one plays or rests the picture, as a tap elsewhere on it does
  const files = (art) => {
    const icons = [...art.querySelectorAll('[data-art-file]')];
    icons.forEach((icon) =>
      icon.addEventListener('click', (event) => {
        event.stopPropagation();
        const depth = (other) => parseInt(other.style.zIndex || '0', 10);
        const top = Math.max(...icons.map(depth));
        const front = icons.every((other) => other === icon || depth(other) < depth(icon));
        if (front && !pointer.matches) {
          live(art, !art.hasAttribute('data-live'));
          return;
        }
        if (!front) icon.style.zIndex = String(top + 1);
        live(art, true);
      })
    );
  };


  // The bonus package has six actual faces. Drag keeps its orientation;
  // the pointer adds a smaller tilt, and Escape/double-click resets it.

  // Rectify the original cover's front quadrilateral into a flat texture.
  // Only the leaf image is cropped; the six box faces stay in 3D.

  // One depth-tested mesh, independent of the theme's CSS 3D contexts.
  const boxControls = (art) => {
    const canvas=art.querySelector('canvas[data-art-box]');
    if(!canvas)return;
    // The printed words in the shop's language (the canvas's data-i18n-*), English as the fallback.
    const say=(key,en)=>canvas.dataset[key]||en;
    // Text no wider than max: a longer translation gets a smaller font; English always fits as drawn before.
    // Letter-spaced like the English label, except in scripts whose letters join or stack (Arabic, Hebrew, Indic, Thai).
    const spaced=(text)=>/[\u0590-\u08FF\u0900-\u0DFF\u0E00-\u0E7F]/.test(text)?text:[...text].join(' ');
    const fitText=(c,text,x,y,max,style,size)=>{c.font=`${style} ${size}px Arial`;const w=c.measureText(text).width;if(w>max)c.font=`${style} ${Math.floor(size*max/w)}px Arial`;c.fillText(text,x,y);};
    const phone=window.matchMedia?.('(max-width: 767px), (max-width: 1023px) and (hover: none) and (pointer: coarse)')||{matches:false};
    if(phone.matches)return;
    const gl=canvas.getContext('webgl',{alpha:true,antialias:true,premultipliedAlpha:false,powerPreference:'low-power',stencil:false});
    if(!gl)return;
    // Lighting runs on a small surface grid, rather than every output
    // pixel. The interpolated result retains the moving studio reflection.
    const vs=`
      attribute vec3 p;
      attribute vec3 n;
      attribute vec2 uv;
      attribute float face;
      uniform vec2 angle;
      uniform float aspect;
      uniform mediump float cameraDistance;
      varying mediump vec2 t;
      varying mediump vec3 normal;
      varying mediump float f;
      varying mediump vec3 lighting;
      vec3 turn(vec3 v) {
        float cx=cos(angle.x), sx=sin(angle.x), cy=cos(angle.y), sy=sin(angle.y);
        vec3 q=vec3(v.x,cx*v.y-sx*v.z,sx*v.y+cx*v.z);
        return vec3(cy*q.x+sy*q.z,q.y,-sy*q.x+cy*q.z);
      }
      void main() {
        vec3 q=turn(p), N=turn(n);
        vec3 L=normalize(vec3(-3.2,4.5,5.0)-q);
        vec3 V=normalize(vec3(0.0,0.0,cameraDistance)-q);
        float diffuse=max(0.0,dot(N,L));
        lighting=vec3(0.62+0.38*diffuse,
          pow(max(0.0,dot(N,normalize(L+V))),44.0),
          pow(max(0.0,1.0-max(0.0,dot(N,V))),3.0)*diffuse);
        q.z-=cameraDistance;
        float lens=1.0/tan(0.366519143);
        gl_Position=vec4(q.x*lens/aspect,q.y*lens,-1.01005025*q.z-0.201005025,-q.z);
        t=uv; normal=N; f=face;
      }
    `;
    // Extend the cover's edge gradient into its padding, so the front
    // stays one continuous surface rather than an inset photograph.
    const fs=`
      precision mediump float;
      uniform sampler2D cover;
      uniform sampler2D backCover;
      uniform sampler2D grainMap;
      uniform mat3 map;
      varying mediump vec2 t;
      varying mediump float f;
      varying mediump vec3 lighting;
      void main() {
        vec3 satin=mix(vec3(0.012),vec3(0.078,0.081,0.083),smoothstep(0.0,1.0,1.0-t.y));
        vec3 color=satin;
        float darkInk=1.0;
        if(f<0.5) {
          vec2 insetUV=clamp((t-vec2(0.06,0.045))/vec2(0.88,0.91),0.0,1.0);
          vec2 coverUV=mix(vec2(0.02,0.025),vec2(0.98,0.98),insetUV);
          vec3 at=map*vec3(coverUV,1.0);
          color=texture2D(cover,at.xy/at.z).rgb;
          float luminance=dot(color,vec3(0.2126,0.7152,0.0722));
          darkInk=1.0-smoothstep(0.12,0.55,luminance);
          color=mix(color,satin,darkInk*0.58);
        } else if(f>=0.5 && f<1.5) {
          color=texture2D(backCover,t).rgb;
          float luminance=dot(color,vec3(0.2126,0.7152,0.0722));
          darkInk=1.0-smoothstep(0.12,0.55,luminance);
        }
        float grain=texture2D(grainMap,t*12.0+vec2(f*0.13)).r-0.5;
        color=color*lighting.x+vec3(grain*0.006*darkInk);
        color+=vec3(0.92,0.96,1.0)*lighting.y*0.16;
        color+=vec3(0.012,0.014,0.016)*lighting.z;
        gl_FragColor=vec4(clamp(color,0.0,1.0),1.0);
      }
    `;
    const compile = (type, source) => {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (gl.getShaderParameter(shader, gl.COMPILE_STATUS)) return shader;
      console.warn('Bonus pack shader:', gl.getShaderInfoLog(shader));
      gl.deleteShader(shader);
      return null;
    };
    const vertex = compile(gl.VERTEX_SHADER, vs);
    if (!vertex) return;
    const link = (source) => {
      const fragment = compile(gl.FRAGMENT_SHADER, source);
      if (!fragment) return null;
      const program = gl.createProgram();
      gl.attachShader(program, vertex);
      gl.attachShader(program, fragment);
      gl.linkProgram(program);
      gl.deleteShader(fragment);
      if (gl.getProgramParameter(program, gl.LINK_STATUS)) return program;
      console.warn('Bonus pack material:', gl.getProgramInfoLog(program));
      gl.deleteProgram(program);
      return null;
    };
    // Retain the actual 3D mesh if a GPU cannot compile the satin material.
    const simpleMaterial = `
      precision mediump float;
      uniform sampler2D cover;
      uniform sampler2D backCover;
      uniform mat3 map;
      varying mediump vec2 t;
      varying mediump vec3 normal;
      varying mediump float f;
      void main() {
        float light = 0.62 + 0.38 * max(0.0, dot(normalize(normal), normalize(vec3(-0.5, 0.8, 1.2))));
        vec3 color = mix(vec3(0.012), vec3(0.078), 1.0 - t.y);
        if (f < 0.5) {
          vec2 insetUV=clamp((t-vec2(0.06,0.045))/vec2(0.88,0.91),0.0,1.0);
          vec3 at = map * vec3(mix(vec2(0.02, 0.025), vec2(0.98, 0.98), insetUV), 1.0);
          color = texture2D(cover, at.xy / at.z).rgb;
        } else if(f>=0.5 && f<1.5) color=texture2D(backCover,t).rgb;
        gl_FragColor = vec4(color * light, 1.0);
      }
    `;
    const program = link(fs) || link(simpleMaterial);
    gl.deleteShader(vertex);
    if (!program) return;
    gl.useProgram(program);
    const x=1.1,y=1.55,z=.22,bevel=.025;
    const ix=x-bevel,iy=y-bevel,iz=z-bevel;
    const faces=[
      {n:[0,0,1],p:[[-ix,-iy,z],[ix,-iy,z],[ix,iy,z],[-ix,iy,z]]},
      {n:[0,0,-1],p:[[-ix,-iy,-z],[-ix,iy,-z],[ix,iy,-z],[ix,-iy,-z]]},
      {n:[1,0,0],p:[[x,-iy,iz],[x,-iy,-iz],[x,iy,-iz],[x,iy,iz]]},
      {n:[-1,0,0],p:[[-x,-iy,-iz],[-x,-iy,iz],[-x,iy,iz],[-x,iy,-iz]]},
      {n:[0,1,0],p:[[-ix,y,iz],[ix,y,iz],[ix,y,-iz],[-ix,y,-iz]]},
      {n:[0,-1,0],p:[[-ix,-y,-iz],[ix,-y,-iz],[ix,-y,iz],[-ix,-y,iz]]}
    ];
    // Small, real bevels catch the studio light without an expensive
    // post-processing pass. All edges and corners remain sealed.
    const dims=[x,y,z];
    const addFace=(points,normal)=>{
      const [a,b,c]=points,ab=b.map((v,i)=>v-a[i]),ac=c.map((v,i)=>v-a[i]);
      const cross=[ab[1]*ac[2]-ab[2]*ac[1],ab[2]*ac[0]-ab[0]*ac[2],ab[0]*ac[1]-ab[1]*ac[0]];
      if(cross.reduce((sum,v,i)=>sum+v*normal[i],0)<0)points.reverse();
      faces.push({p:points,n:normal});
    };
    for(let a=0;a<3;a++)for(let b=a+1;b<3;b++)for(const sa of [-1,1])for(const sb of [-1,1]) {
      const free=3-a-b,points=[];
      for(const [offset,end] of [[0,-1],[1,-1],[1,1],[0,1]]) {
        const point=[0,0,0];
        point[a]=sa*(dims[a]-(offset?bevel:0));
        point[b]=sb*(dims[b]-(offset?0:bevel));
        point[free]=end*(dims[free]-bevel);points.push(point);
      }
      const normal=[0,0,0];normal[a]=sa/Math.sqrt(2);normal[b]=sb/Math.sqrt(2);addFace(points,normal);
    }
    for(const sx of [-1,1])for(const sy of [-1,1])for(const sz of [-1,1]) {
      const signs=[sx,sy,sz];
      const points=[0,1,2].map(axis=>dims.map((d,i)=>signs[i]*(d-(i===axis?0:bevel))));
      addFace(points,signs.map(s=>s/Math.sqrt(3)));
    }
    const uv=[[0,1],[1,1],[1,0],[0,0]],vertices=[];
    faces.forEach((face,i)=>{
      // Six by eight cells keep the front highlight smooth while lighting
      // only a few hundred vertices in total instead of hundreds of thousands of pixels.
      if(i===0) {
        const point=(u,v)=>[-ix+2*ix*u,-iy+2*iy*v,z,...face.n,u,1-v,i];
        for(let row=0;row<8;row++)for(let col=0;col<6;col++) {
          const a=point(col/6,row/8),b=point((col+1)/6,row/8);
          const c=point((col+1)/6,(row+1)/8),d=point(col/6,(row+1)/8);
          [a,b,c,a,c,d].forEach(p=>vertices.push(...p));
        }
      } else {
        const coords=i===1?[[1,1],[1,0],[0,0],[0,1]]:uv;
        const order=face.p.length===3?[0,1,2]:[0,1,2,0,2,3];
        order.forEach(j=>vertices.push(...face.p[j],...face.n,...coords[j],i));
      }
    });
    // Share only identical complete vertices. Face normals, UV seams and
    // triangle order stay exact, preserving the existing studio lighting.
    const packed=new Float32Array(vertices),unique=[],indices=[],lookup=new Map();
    for(let offset=0;offset<packed.length;offset+=9) {
      const vertex=packed.subarray(offset,offset+9),key=vertex.join(',');
      let index=lookup.get(key);
      if(index===undefined){index=unique.length/9;lookup.set(key,index);unique.push(...vertex);}
      indices.push(index);
    }
    const indexCount=indices.length;
    const buffer=gl.createBuffer();gl.bindBuffer(gl.ARRAY_BUFFER,buffer);gl.bufferData(gl.ARRAY_BUFFER,new Float32Array(unique),gl.STATIC_DRAW);
    const indexBuffer=gl.createBuffer();gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER,indexBuffer);gl.bufferData(gl.ELEMENT_ARRAY_BUFFER,new Uint16Array(indices),gl.STATIC_DRAW);
    [['p',3,0],['n',3,3],['uv',2,6],['face',1,8]].forEach(([name,size,offset])=>{const l=gl.getAttribLocation(program,name);gl.enableVertexAttribArray(l);gl.vertexAttribPointer(l,size,gl.FLOAT,false,36,offset*4);});
    gl.enable(gl.DEPTH_TEST);gl.enable(gl.CULL_FACE);gl.cullFace(gl.BACK);gl.clearColor(0,0,0,0);
    const angle=gl.getUniformLocation(program,'angle'),aspectUniform=gl.getUniformLocation(program,'aspect'),distanceUniform=gl.getUniformLocation(program,'cameraDistance'),mapUniform=gl.getUniformLocation(program,'map');
    // A tiny repeating grain texture replaces procedural noise per pixel.
    const grainTexture=gl.createTexture(),grainBytes=new Uint8Array(64*64*4);
    let seed=29;
    for(let i=0;i<grainBytes.length;i+=4) {
      seed=(Math.imul(seed,1664525)+1013904223)>>>0;
      const value=seed>>>24;
      grainBytes[i]=grainBytes[i+1]=grainBytes[i+2]=value;grainBytes[i+3]=255;
    }
    gl.activeTexture(gl.TEXTURE1);gl.bindTexture(gl.TEXTURE_2D,grainTexture);
    gl.texImage2D(gl.TEXTURE_2D,0,gl.RGBA,64,64,0,gl.RGBA,gl.UNSIGNED_BYTE,grainBytes);
    gl.generateMipmap(gl.TEXTURE_2D);
    gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MIN_FILTER,gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MAG_FILTER,gl.LINEAR);
    gl.uniform1i(gl.getUniformLocation(program,'grainMap'),1);
    gl.uniform1i(gl.getUniformLocation(program,'cover'),0);
    gl.activeTexture(gl.TEXTURE0);
    const texture=gl.createTexture();gl.bindTexture(gl.TEXTURE_2D,texture);
    [[gl.TEXTURE_WRAP_S,gl.CLAMP_TO_EDGE],[gl.TEXTURE_WRAP_T,gl.CLAMP_TO_EDGE],[gl.TEXTURE_MIN_FILTER,gl.LINEAR],[gl.TEXTURE_MAG_FILTER,gl.LINEAR]].forEach(([p,v])=>gl.texParameteri(gl.TEXTURE_2D,p,v));
    const fallback=document.createElement('canvas');fallback.width=512;fallback.height=720;const ctx=fallback.getContext('2d');
    const gradient=ctx.createLinearGradient(0,0,512,720);gradient.addColorStop(0,'#424242');gradient.addColorStop(1,'#070707');ctx.fillStyle=gradient;ctx.fillRect(0,0,512,720);
    ctx.fillStyle='#f5f5f7';ctx.textAlign='center';ctx.font='italic 900 150px Arial';ctx.fillText('5x',256,174);ctx.fillStyle='#ededed';ctx.fillRect(136,230,240,240);
    ctx.fillStyle='#111';ctx.font='bold 45px Arial';ctx.fillText('PSET',256,422);ctx.strokeStyle='#111';ctx.lineWidth=8;ctx.lineCap='round';
    [290,345].forEach((at,i)=>{ctx.beginPath();ctx.moveTo(175,at);ctx.lineTo(338,at);ctx.stroke();ctx.beginPath();ctx.arc(i?285:225,at,18,0,Math.PI*2);ctx.fillStyle='#ededed';ctx.fill();ctx.stroke();});
    ctx.fillStyle='#f5f5f7';fitText(ctx,say('i18nBonus','Bonus'),256,586,440,'italic 900',86);fitText(ctx,say('i18nPresets','Presets'),256,672,440,'italic 900',86);
    gl.texImage2D(gl.TEXTURE_2D,0,gl.RGBA,gl.RGBA,gl.UNSIGNED_BYTE,fallback);gl.uniformMatrix3fv(mapUniform,false,new Float32Array([1,0,0,0,1,0,0,0,1]));
    // The rear label is a cached texture, using the store's actual logo.
    let textureDirty=true;
    const backTexture=gl.createTexture(),back=document.createElement('canvas');
    back.width=back.height=1024;
    const backCtx=back.getContext('2d');
    const uploadBack=()=>{
      gl.activeTexture(gl.TEXTURE2);gl.bindTexture(gl.TEXTURE_2D,backTexture);
      gl.texImage2D(gl.TEXTURE_2D,0,gl.RGBA,gl.RGBA,gl.UNSIGNED_BYTE,back);
      gl.generateMipmap(gl.TEXTURE_2D);
      gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MIN_FILTER,gl.LINEAR_MIPMAP_LINEAR);
      gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MAG_FILTER,gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_S,gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_T,gl.CLAMP_TO_EDGE);
      textureDirty=true;
      gl.uniform1i(gl.getUniformLocation(program,'backCover'),2);
      gl.activeTexture(gl.TEXTURE0);
    };
    const drawBack=(logo=null)=>{
      backCtx.setTransform(2,0,0,1024/720,0,0);
      const bg=backCtx.createLinearGradient(0,0,0,720);
      bg.addColorStop(0,'#1b1d20');bg.addColorStop(1,'#070808');
      backCtx.fillStyle=bg;backCtx.fillRect(0,0,512,720);
      backCtx.fillStyle='#e5e5e7';backCtx.textAlign='center';
      if(logo)backCtx.drawImage(logo,200,238,112,112);
      else {backCtx.font='600 30px Arial';backCtx.fillText('Adobe Basics',256,308);}
      backCtx.font='500 28px Arial';backCtx.fillText('adobebasics.com',256,404);
      // Printed details are baked once into the existing rear texture.
      backCtx.strokeStyle='#34363a';backCtx.lineWidth=1;backCtx.strokeRect(34,34,444,652);
      backCtx.fillStyle='#c6c9cf';fitText(backCtx,spaced(say('i18nBackTitle','BONUS PRESETS')),256,92,400,'600',22);
      backCtx.fillStyle='#82c9a2';backCtx.fillRect(232,532,48,2);
      backCtx.fillStyle='#c6c9cf';fitText(backCtx,say('i18nMadeFor','Made for Premiere Pro'),256,574,400,'500',24);
      backCtx.fillStyle='#b1b5bc';fitText(backCtx,say('i18nTagline','Create. Refine. Make it yours.'),256,604,400,'400',22);
      uploadBack();
    };
    drawBack();
    const logo=new Image();logo.crossOrigin='anonymous';
    logo.onload=()=>{
      if(disposed)return;
      try {
        const stamp=document.createElement('canvas');stamp.width=stamp.height=256;
        const ctx=stamp.getContext('2d');
        const ratio=Math.min(256/logo.naturalWidth,256/logo.naturalHeight);
        const width=logo.naturalWidth*ratio,height=logo.naturalHeight*ratio;
        ctx.drawImage(logo,(256-width)/2,(256-height)/2,width,height);
        drawBack(stamp);wake();
      } catch(error) { /* The readable brand label remains if the logo is unavailable. */ }
    };
    if(canvas.dataset.boxLogo)logo.src=canvas.dataset.boxLogo;
    let rx=-.10,ry=-.42,targetRX=rx,targetRY=ry,baseRX=rx,baseRY=ry,drag=null,frame=0,previous=null;
    let visible=true,disposed=false,sizeDirty=true,aspect=1,distance=6,lastDraw=-Infinity,vRX=0,vRY=0;
    let coverReady=false,revealVisible=!('IntersectionObserver' in window),revealStarted=false,revealDone=still.matches,revealElapsed=0,cueX=0,cueY=0;
    let drawnRX=NaN,drawnRY=NaN,idleTimer=0,returning=false;
    const clearIdle=()=>{clearTimeout(idleTimer);idleTimer=0;};
    const queueReset=()=>{
      clearIdle();
      if(disposed||drag||phone.matches)return;
      idleTimer=setTimeout(()=>{idleTimer=0;if(!disposed&&!drag)reset();},3000);
    };
    const canDraw=()=>!disposed&&!phone.matches&&visible&&!document.hidden&&canvas.isConnected;
    const measure=()=>{
      const cw=Math.max(1,canvas.clientWidth),ch=Math.max(1,canvas.clientHeight);
      // Bound both retina scaling and unusually large previews. A 2x display
      // does 61% less pixel work at the same CSS size with the 1.25x cap.
      const scale=Math.min(window.devicePixelRatio||1,1.25,960/cw,960/ch,Math.sqrt(480000/(cw*ch)));
      const w=Math.max(1,Math.floor(cw*scale)),h=Math.max(1,Math.floor(ch*scale));
      if(canvas.width!==w||canvas.height!==h){canvas.width=w;canvas.height=h;textureDirty=true;}
      aspect=w/h;
      const halfFov=Math.atan(Math.tan(.366519143)*Math.min(1,aspect));
      distance=2.1/Math.sin(halfFov)+.35;
      gl.viewport(0,0,w,h);gl.uniform1f(aspectUniform,aspect);gl.uniform1f(distanceUniform,distance);
      sizeDirty=false;
    };
    const render=()=>{
      if(sizeDirty)measure();
      const nextRX=rx+cueX,nextRY=ry+cueY;
      // Visibility notifications, stationary pointers and the reveal's
      // initial delay do not require another identical GPU frame.
      if(!textureDirty&&nextRX===drawnRX&&nextRY===drawnRY)return;
      gl.uniform2f(angle,nextRX,nextRY);
      gl.clear(gl.COLOR_BUFFER_BIT|gl.DEPTH_BUFFER_BIT);gl.drawElements(gl.TRIANGLES,indexCount,gl.UNSIGNED_SHORT,0);
      drawnRX=nextRX;drawnRY=nextRY;textureDirty=false;
    };
    const tick=now=>{
      frame=0;
      if(!canDraw()){previous=null;return;}
      const gap=previous===null?16:Math.max(0,now-previous);
      // A 120/144 Hz screen should not double the work for this preview.
      if(now-lastDraw<1000/60-1){frame=requestAnimationFrame(tick);return;}
      previous=now;
      const dt=Math.min(64,gap),w=drag?.04:.024;
      if(still.matches){rx=targetRX;ry=targetRY;vRX=vRY=0;}
      else if(returning) {
        // Exact damped spring: a slower return with ~2% overshoot. Preserve
        // velocity when interrupted, and stay stable across refresh rates.
        const frequency=.012,damping=.78,decayRate=frequency*damping;
        const damped=frequency*Math.sqrt(1-damping*damping);
        const decay=Math.exp(-decayRate*dt),cos=Math.cos(damped*dt),sin=Math.sin(damped*dt);
        const dx=rx-targetRX,dy=ry-targetRY;
        const bx=(vRX+decayRate*dx)/damped,by=(vRY+decayRate*dy)/damped;
        const nx=dx*cos+bx*sin,ny=dy*cos+by*sin;
        vRX=(damped*(-dx*sin+bx*cos)-decayRate*nx)*decay;
        vRY=(damped*(-dy*sin+by*cos)-decayRate*ny)*decay;
        rx=targetRX+nx*decay;ry=targetRY+ny*decay;
      }
      else {
        // Both axes use the same decay. Keep the exact spring equation
        // without allocating intermediate arrays on every frame.
        const decay=Math.exp(-w*dt),dx=rx-targetRX,dy=ry-targetRY;
        const cx=vRX+w*dx,cy=vRY+w*dy;
        rx=targetRX+(dx+cx*dt)*decay;ry=targetRY+(dy+cy*dt)*decay;
        vRX=(vRX-w*cx*dt)*decay;vRY=(vRY-w*cy*dt)*decay;
      }
      if(rx<-.55||rx>.55){rx=Math.max(-.55,Math.min(.55,rx));vRX=0;}
      const moving=Math.abs(targetRX-rx)+Math.abs(targetRY-ry)>.0005||Math.abs(vRX)+Math.abs(vRY)>.00001;
      if(!moving){rx=targetRX;ry=targetRY;vRX=vRY=0;returning=false;}
      // A single gentle turn hints at drag interaction. The cosine-shaped
      // cue begins and ends at zero velocity and never loops at rest.
      if(revealStarted&&!revealDone&&revealVisible) {
        if(still.matches)revealDone=true;
        else {
          revealElapsed+=dt;
          const progress=Math.max(0,Math.min((revealElapsed-400)/2600,1));
          const turn=Math.pow(Math.sin(Math.PI*progress),2);
          cueX=.05*turn;cueY=-.38*turn;
          if(progress===1)revealDone=true;
        }
        if(revealDone)cueX=cueY=0;
      }
      render();
      lastDraw=now;
      if(moving||revealStarted&&!revealDone&&revealVisible)frame=requestAnimationFrame(tick);else previous=null;
    };
    const wake=()=>{if(canDraw()&&!frame)frame=requestAnimationFrame(tick);};
    const suspend=()=>{cancelAnimationFrame(frame);frame=0;previous=null;};
    const startReveal=()=>{
      if(!coverReady||!revealVisible||revealStarted||revealDone||document.hidden)return;
      revealStarted=true;wake();
    };
    const stopReveal=()=>{
      // Hand off the exact visible pose without jumping to the resting one.
      revealDone=true;rx+=cueX;ry+=cueY;cueX=cueY=0;
    };
    const image=new Image();image.crossOrigin='anonymous';
    image.onload=()=>{
      if(disposed)return;
      const from=[[0,0],[1,0],[1,1],[0,1]],to=[[.287,.078],[.815,.04],[.809,.893],[.295,.967]],rows=[];
      from.forEach(([u,v],i)=>{const [s,t]=to[i];rows.push([u,v,1,0,0,0,-s*u,-s*v,s],[0,0,0,u,v,1,-t*u,-t*v,t]);});
      for(let col=0;col<8;col++){let pivot=col;for(let row=col+1;row<8;row++)if(Math.abs(rows[row][col])>Math.abs(rows[pivot][col]))pivot=row;[rows[col],rows[pivot]]=[rows[pivot],rows[col]];const d=rows[col][col];for(let j=col;j<=8;j++)rows[col][j]/=d;for(let row=0;row<8;row++){if(row===col)continue;const factor=rows[row][col];for(let j=col;j<=8;j++)rows[row][j]-=factor*rows[col][j];}}
      const h=rows.map(row=>row[8]);
      try{
        // Upload a bounded, mipmapped texture once. Normalized cover mapping
        // keeps the original artwork intact at every angle.
        const cover=document.createElement('canvas');cover.width=cover.height=1024;
        cover.getContext('2d').drawImage(image,0,0,1024,1024);
        gl.activeTexture(gl.TEXTURE0);gl.bindTexture(gl.TEXTURE_2D,texture);
        gl.texImage2D(gl.TEXTURE_2D,0,gl.RGBA,gl.RGBA,gl.UNSIGNED_BYTE,cover);
        gl.generateMipmap(gl.TEXTURE_2D);textureDirty=true;
        gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MIN_FILTER,gl.LINEAR_MIPMAP_LINEAR);
        gl.uniformMatrix3fv(mapUniform,false,new Float32Array([h[0],h[3],h[6],h[1],h[4],h[7],h[2],h[5],1]));}catch(error){/* Retain fallback for unavailable CDN textures. */}coverReady=true;startReveal();wake();
    };
    image.onerror=()=>{if(disposed)return;coverReady=true;startReveal();wake();};
    image.src=canvas.dataset.boxSource;
    let resizer=null,visibility=null;
    if('ResizeObserver' in window) {
      resizer=new ResizeObserver(()=>{sizeDirty=true;wake();});resizer.observe(canvas);
    } else window.addEventListener('resize',onResize);
    function onResize(){sizeDirty=true;wake();}
    if('IntersectionObserver' in window) {
      visibility=new IntersectionObserver(([entry])=>{
        visible=entry.isIntersecting;
        revealVisible=visible&&entry.intersectionRatio>=.55;
        if(visible){startReveal();wake();}else suspend();
      },{threshold:[0,.55]});visibility.observe(canvas);
    }
    const onVisibility=()=>{if(document.hidden)suspend();else {startReveal();wake();}};
    const onPhone=()=>{if(phone.matches){clearIdle();suspend();mobileCutout(art);}else {startReveal();wake();queueReset();}};
    phone.addEventListener?.('change',onPhone);
    const onUnload=e=>{if(e.target.contains(canvas))dispose();};
    const dispose=()=>{
      if(disposed)return;disposed=true;clearIdle();suspend();phone.removeEventListener?.('change',onPhone);resizer?.disconnect();visibility?.disconnect();
      document.removeEventListener('visibilitychange',onVisibility);
      document.removeEventListener('shopify:section:unload',onUnload);
      window.removeEventListener('resize',onResize);image.onload=image.onerror=logo.onload=null;
      gl.deleteTexture(texture);gl.deleteTexture(grainTexture);gl.deleteTexture(backTexture);gl.deleteBuffer(buffer);gl.deleteBuffer(indexBuffer);gl.deleteProgram(program);
    };
    document.addEventListener('visibilitychange',onVisibility);
    document.addEventListener('shopify:section:unload',onUnload);
    canvas.addEventListener('pointerdown',e=>{if(!e.isPrimary||e.button!==0)return;clearIdle();returning=false;stopReveal();baseRX=rx;baseRY=ry;drag={id:e.pointerId,x:e.clientX,y:e.clientY,rx:baseRX,ry:baseRY};canvas.setPointerCapture(e.pointerId);canvas.setAttribute('data-dragging','');e.stopPropagation();});
    canvas.addEventListener('pointermove',e=>{returning=false;stopReveal();if(drag&&drag.id===e.pointerId){baseRY=drag.ry+(e.clientX-drag.x)*.0045;baseRX=Math.max(-.45,Math.min(.45,drag.rx-(e.clientY-drag.y)*.0035));targetRX=baseRX;targetRY=baseRY;}else{const b=canvas.getBoundingClientRect();targetRX=baseRX-Math.max(-.5,Math.min(.5,(e.clientY-b.top)/b.height-.5))*.12;targetRY=baseRY+Math.max(-.5,Math.min(.5,(e.clientX-b.left)/b.width-.5))*.22;}wake();queueReset();});
    const drop=e=>{if(!drag||e.pointerId!==drag.id)return;drag=null;canvas.removeAttribute('data-dragging');wake();queueReset();};['pointerup','pointercancel','lostpointercapture'].forEach(type=>canvas.addEventListener(type,drop));
    (art.closest('[data-bonus-row]')||art).addEventListener('pointerleave',()=>{if(drag)return;targetRX=baseRX;targetRY=baseRY;wake();queueReset();});
    const reset=()=>{
      clearIdle();stopReveal();returning=true;
      // Keep the return on the shortest arc after any full revolutions.
      ry=-.42+Math.atan2(Math.sin(ry+.42),Math.cos(ry+.42));
      baseRX=targetRX=-.10;baseRY=targetRY=-.42;wake();
    };canvas.addEventListener('click',e=>e.stopPropagation());canvas.addEventListener('dblclick',reset);
    canvas.addEventListener('keydown',e=>{if(!['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Escape'].includes(e.key))return;e.preventDefault();stopReveal();if(e.key==='Escape'){reset();return;}returning=false;baseRY+=e.key==='ArrowRight'?.14:e.key==='ArrowLeft'?-.14:0;baseRX=Math.max(-.45,Math.min(.45,baseRX+(e.key==='ArrowDown'?.09:e.key==='ArrowUp'?-.09:0)));targetRX=baseRX;targetRY=baseRY;wake();queueReset();});
    wake();
  };

  // A one-time 2D cutout keeps mobile static and preserves enclosed white
  // lettering. Only pale pixels connected to the photo's border are removed.
  const cutoutCache=new Map();
  const mobileCutout=(art)=>{
    const picture=art.querySelector('img.upp-bonus__box-image');
    if(!picture||picture.hasAttribute('data-cutout-ready'))return;
    const url=picture.currentSrc||picture.src;
    let task=cutoutCache.get(url);
    if(!task){
      task=new Promise(resolve=>{
        const source=new Image();source.crossOrigin='anonymous';
        source.onload=()=>{
          try{
            const canvas=document.createElement('canvas');
            canvas.width=source.naturalWidth;canvas.height=source.naturalHeight;
            const context=canvas.getContext('2d',{willReadFrequently:true});
            context.drawImage(source,0,0);
            const pixels=context.getImageData(0,0,canvas.width,canvas.height);
            const {data,width,height}=pixels,total=width*height;
            const queue=new Uint32Array(total),visited=new Uint8Array(total);let read=0,write=0;
            const visit=index=>{
              const offset=index*4;
              if(visited[index])return;
              visited[index]=1;
              // Transparent padding is part of the exterior too. Traversing
              // it is essential to reach an inset opaque photo background.
              const low=Math.min(data[offset],data[offset+1],data[offset+2]);
              const high=Math.max(data[offset],data[offset+1],data[offset+2]);
              if(data[offset+3]!==0&&(low<170||high-low>90))return;
              data[offset+3]=0;queue[write++]=index;
            };
            for(let x=0;x<width;x++){visit(x);visit((height-1)*width+x);}
            for(let y=1;y<height-1;y++){visit(y*width);visit(y*width+width-1);}
            while(read<write){
              const index=queue[read++],x=index%width;
              if(x>0)visit(index-1);if(x<width-1)visit(index+1);
              if(index>=width)visit(index-width);if(index<total-width)visit(index+width);
            }
            context.putImageData(pixels,0,0);resolve(canvas.toDataURL('image/png'));
          }catch(error){resolve(null);}
        };
        source.onerror=()=>resolve(null);source.src=url;
      });
      cutoutCache.set(url,task);
    }
    task.then(url=>{
      if(!picture.isConnected)return;
      if(url){picture.removeAttribute('srcset');picture.removeAttribute('sizes');picture.src=url;}
      if(url)picture.setAttribute('data-cutout-ready','');
      else cutoutCache.delete(picture.currentSrc||picture.src);
    });
  };

  const init = (art, index) => {
    if (art.hasAttribute('data-art-ready')) return;
    art.setAttribute('data-art-ready', '');
    // The 3D preview has its own interaction. Defer its context, shaders
    // and image upload until it approaches the viewport.
    if(art.querySelector('canvas[data-art-box]')) {
      const phone=window.matchMedia?.('(max-width: 767px), (max-width: 1023px) and (hover: none) and (pointer: coarse)')||{matches:false};
      let loader=null;
      const disarm=()=>{
        loader?.disconnect();phone.removeEventListener?.('change',prepare);
        document.removeEventListener('shopify:section:unload',unload);
      };
      const unload=e=>{if(e.target.contains(art))disarm();};
      const prepare=()=>{
        if(!art.isConnected){disarm();return;}
        if(phone.matches){
          const picture=art.querySelector('img.upp-bonus__box-image');
          if(picture?.complete&&picture.naturalWidth)mobileCutout(art);
          else picture?.addEventListener('load',()=>mobileCutout(art),{once:true});
          return;
        }
        if('IntersectionObserver' in window) {
          if(loader)return;
          loader=new IntersectionObserver(([entry])=>{
            if(!art.isConnected){disarm();return;}
            if(!entry.isIntersecting||phone.matches)return;
            disarm();boxControls(art);
          },{rootMargin:'240px 0px'});
          loader.observe(art);
        } else {disarm();boxControls(art);}
      };
      phone.addEventListener?.('change',prepare);
      document.addEventListener('shopify:section:unload',unload);
      prepare();
      return;
    }
    const card = art.closest('[data-bonus-row]') || art.parentElement;
    knobs(art);
    files(art);
    boxControls(art);

    if (pointer.matches) {
      let frame = 0;
      let px = 0;
      let py = 0;
      const nudge = () => {
        frame = 0;
        art.style.setProperty('--px', px.toFixed(3));
        art.style.setProperty('--py', py.toFixed(3));
      };
      card.addEventListener('pointerenter', () => live(art, true));
      card.addEventListener('pointerleave', () => {
        live(art, false);
        px = 0;
        py = 0;
        if (!frame) frame = requestAnimationFrame(nudge);
      });
      art.addEventListener('pointermove', (event) => {
        if (still.matches) return;
        const box = art.getBoundingClientRect();
        px = Math.max(-1, Math.min(1, ((event.clientX - box.left) / box.width - 0.5) * 2));
        py = Math.max(-1, Math.min(1, ((event.clientY - box.top) / box.height - 0.5) * 2));
        if (!frame) frame = requestAnimationFrame(nudge);
      });
      return;
    }

    // touch: play once the card is in view, the four taking turns
    let wait = 0;
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          // only the latest change counts (a fast scroll can bring several)
          const entry = entries[entries.length - 1];
          clearTimeout(wait);
          if (entry.isIntersecting) wait = setTimeout(() => live(art, true), still.matches ? 0 : 500 + (index % 4) * 450);
          else live(art, false);
        },
        { threshold: 0.6 }
      ).observe(art);
    }
    art.addEventListener('click', () => {
      clearTimeout(wait);
      live(art, !art.hasAttribute('data-live'));
    });
  };

  const arm = (root) => root.querySelectorAll('[data-bonus-art]').forEach(init);
  arm(document);
  document.addEventListener('shopify:section:load', (event) => arm(event.target));
})();







